import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Cpu,
  Network,
  ShieldCheck,
  Layers,
  Radio,
} from "lucide-react";
import { BRAND, HERO_STATS } from "../lib/constants";

const ORBIT_MODULES = [
  { Icon: Cpu, label: "CRM core", angle: -45 },
  { Icon: Network, label: "LP bridge", angle: 35 },
  { Icon: ShieldCheck, label: "Risk layer", angle: 145 },
  { Icon: Layers, label: "Hosting", angle: 215 },
];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden hero-lane min-h-[min(92vh,880px)]"
    >
      <div className="absolute inset-0 theme-hero-bg" />
      <div className="absolute inset-0 hero-topo pointer-events-none" aria-hidden />
      <div className="absolute -right-[20%] top-[10%] w-[70%] h-[80%] hero-arc-glow pointer-events-none" aria-hidden />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex gap-0 lg:gap-6">
          {/* Vertical rail — not a centered NEW pill */}
          <div
            className="hidden lg:flex flex-col items-center justify-between py-10 pr-6 border-r border-[#00C2FF]/20 shrink-0 w-14"
            aria-hidden
          >
            <span className="hero-rail-text font-mono text-[10px] uppercase tracking-[0.35em] text-[#00C2FF]">
              Platform
            </span>
            <span className="font-display text-2xl font-bold text-[#00C2FF]/30">01</span>
            <span className="hero-rail-text font-mono text-[10px] uppercase tracking-[0.35em] text-[#00C2FF]/60">
              2026
            </span>
          </div>

          <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center py-12 sm:py-16 lg:py-20">
            {/* Left: copy — left-aligned, no center stack */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }}
              className="lg:col-span-7 xl:col-span-6 text-left"
            >
              <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#9FE3FF] mb-6 lg:hidden">
                {BRAND.tagline}
              </p>

              <h1
                data-testid="hero-heading"
                className="font-display font-extrabold leading-[1.05] tracking-tight text-[2.4rem] sm:text-[3.1rem] lg:text-[3.5rem] xl:text-[3.85rem]"
              >
                <span className="theme-heading block">Trading desks run on</span>
                <span className="theme-heading block mt-1">infrastructure.</span>
                <span className="hero-underline-word block mt-3 sm:mt-4 text-[#00C2FF]">
                  We build yours end-to-end.
                </span>
              </h1>

              <p className="theme-subtle mt-7 text-base sm:text-lg max-w-lg leading-relaxed border-l-2 border-[#003E8C] pl-5">
                {BRAND.name} delivers CRM, liquidity bridges, copy engines, and MT hosting as
                owned modules — one roadmap, one engineering team, zero patchwork vendors.
              </p>

              <div className="mt-10 flex flex-col items-start gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-services"
                  className="hero-cta-outline inline-flex items-center gap-3 px-8 py-4 rounded-sm font-semibold text-sm sm:text-base uppercase tracking-wide"
                >
                  Request technical scope
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a
                  href="#services"
                  data-testid="hero-cta-demo"
                  className="group inline-flex items-center gap-3 text-sm font-mono uppercase tracking-[0.2em] theme-muted hover:text-[#00C2FF] transition-colors"
                >
                  <span className="w-8 h-px bg-[#00C2FF]/50 group-hover:w-12 transition-all" />
                  Browse delivery modules
                  <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Vertical stats on mobile/tablet row; desktop moves to orbit column footer */}
              <ul className="mt-12 flex flex-wrap gap-8 lg:hidden">
                {HERO_STATS.map((s) => (
                  <li
                    key={s.label}
                    data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <div className="font-display text-2xl font-extrabold text-[#00C2FF] tabular-nums">
                      {s.value}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] theme-muted mt-1">
                      {s.label}
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Right: orbital diagram — not charts / dashboards / centered mockups */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.12 }}
              className="lg:col-span-5 xl:col-span-6 relative flex items-center justify-center min-h-[320px] sm:min-h-[380px]"
              data-testid="hero-orbit"
            >
              <div className="hero-orbit-core relative w-[min(100%,340px)] aspect-square">
                <div className="absolute inset-[8%] rounded-full border border-[#00C2FF]/15" />
                <div className="absolute inset-[22%] rounded-full border border-dashed border-[#00C2FF]/25 hero-orbit-spin-slow" />
                <div className="absolute inset-[36%] rounded-full bg-[#003E8C]/40 border border-[#00C2FF]/30 flex flex-col items-center justify-center text-center px-6">
                  <Radio className="w-6 h-6 text-[#00C2FF] mb-2 pulse-cyan" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] theme-muted">
                    Unified stack
                  </span>
                  <span className="font-display text-lg font-bold theme-heading mt-1">
                    {BRAND.name.split(" ").slice(-2).join(" ")}
                  </span>
                </div>

                {ORBIT_MODULES.map((mod, i) => {
                  const radius = 46;
                  const rad = (mod.angle * Math.PI) / 180;
                  const x = 50 + radius * Math.cos(rad);
                  const y = 50 + radius * Math.sin(rad);
                  return (
                    <motion.div
                      key={mod.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.25 + i * 0.08 }}
                      className="absolute w-[88px] sm:w-[100px] -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${x}%`, top: `${y}%` }}
                    >
                      <div className="hero-orbit-node rounded-lg px-3 py-2.5 text-center shadow-lg">
                        <mod.Icon className="w-4 h-4 text-[#00C2FF] mx-auto mb-1" strokeWidth={1.75} />
                        <span className="font-mono text-[8px] sm:text-[9px] uppercase tracking-wider theme-heading block leading-tight">
                          {mod.label}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <ul className="hidden lg:flex absolute -bottom-2 right-0 flex-col gap-5 text-right">
                {HERO_STATS.map((s) => (
                  <li
                    key={s.label}
                    data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <div className="font-display text-3xl font-extrabold text-[#00C2FF] tabular-nums leading-none">
                      {s.value}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] theme-muted mt-1">
                      {s.label}
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Module lane — horizontal strip, not trust chips or bento */}
      <div className="relative z-10 border-t border-b theme-divider hero-module-lane overflow-hidden">
        <div className="flex hero-lane-track whitespace-nowrap py-3">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex shrink-0">
              {[
                "Forex CRM",
                "Liquidity bridge",
                "Trade copier",
                "Prop evaluations",
                "MT5 dedicated",
                "Risk automation",
                "Payment rails",
                "Custom APIs",
              ].map((label) => (
                <span
                  key={`${dup}-${label}`}
                  className="mx-6 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#00C2FF]/70"
                >
                  {label}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
